import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import {
  ArrowLeft, ArrowRight, BookOpen, Calculator, CheckCircle, XCircle,
  Star, Trophy, Play, Volume2, Loader2, Target, Lock, Gift,
  ChevronRight, Award, Puzzle, Gamepad2, Brain, Pencil, FlaskConical
} from 'lucide-react';

const CurriculumFlow = () => {
  const { childId, subject } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [child, setChild] = useState(null);
  const [curriculum, setCurriculum] = useState(null);
  const [currentLevel, setCurrentLevel] = useState(1);
  const [currentActivity, setCurrentActivity] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [levelComplete, setLevelComplete] = useState(false);

  // Activity types for each level
  const LEVEL_ACTIVITIES = {
    1: [
      { type: 'lesson', name: 'Introduction', icon: BookOpen, xp: 10 },
      { type: 'quiz', name: 'Quick Check', icon: Brain, xp: 25 },
      { type: 'game', name: 'Practice Game', icon: Gamepad2, xp: 30 },
      { type: 'puzzle', name: 'Puzzle Challenge', icon: Puzzle, xp: 40 },
      { type: 'assessment', name: 'Level Test', icon: Target, xp: 50 },
    ],
    2: [
      { type: 'lesson', name: 'Advanced Concepts', icon: BookOpen, xp: 15 },
      { type: 'quiz', name: 'Knowledge Check', icon: Brain, xp: 30 },
      { type: 'game', name: 'Skill Builder', icon: Gamepad2, xp: 35 },
      { type: 'puzzle', name: 'Mind Bender', icon: Puzzle, xp: 45 },
      { type: 'assessment', name: 'Mastery Test', icon: Target, xp: 60 },
    ],
    3: [
      { type: 'lesson', name: 'Expert Topics', icon: BookOpen, xp: 20 },
      { type: 'quiz', name: 'Expert Quiz', icon: Brain, xp: 35 },
      { type: 'game', name: 'Challenge Mode', icon: Gamepad2, xp: 40 },
      { type: 'puzzle', name: 'Master Puzzle', icon: Puzzle, xp: 50 },
      { type: 'assessment', name: 'Final Assessment', icon: Target, xp: 75 },
    ],
  };

  const subjectConfig = {
    reading: { name: 'Reading', icon: BookOpen, color: '#10B981', bgColor: 'bg-emerald-500' },
    math: { name: 'Math', icon: Calculator, color: '#EF4444', bgColor: 'bg-red-500' },
    writing: { name: 'Writing', icon: Pencil, color: '#06B6D4', bgColor: 'bg-cyan-500' },
    science: { name: 'Science', icon: FlaskConical, color: '#8B5CF6', bgColor: 'bg-purple-500' },
  };

  const config = subjectConfig[subject] || subjectConfig.reading;

  useEffect(() => {
    fetchData();
  }, [childId, subject]);

  const fetchData = async () => {
    try {
      const [childRes, progressRes] = await Promise.all([
        api.get(`/children/${childId}`),
        api.get(`/adaptive/progress/${childId}`).catch(() => ({ data: {} }))
      ]);
      
      setChild(childRes.data);
      
      // Get subject-specific progress
      const subjectProgress = progressRes.data?.subjects?.[subject];
      if (subjectProgress) {
        setCurrentLevel(subjectProgress.current_level || 1);
        // Calculate current activity based on progress
        const completedInLevel = subjectProgress.activities_completed_in_level || 0;
        setCurrentActivity(Math.min(completedInLevel, 4));
      }
      
      // Build curriculum structure
      setCurriculum({
        totalLevels: 3,
        levelsCompleted: subjectProgress?.levels_completed || 0,
        totalXP: subjectProgress?.total_xp || 0,
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleActivityClick = (levelNum, activityIndex) => {
    // Check if activity is unlocked
    if (levelNum > currentLevel) return;
    if (levelNum === currentLevel && activityIndex > currentActivity) return;

    const activity = LEVEL_ACTIVITIES[levelNum][activityIndex];
    
    // Navigate to appropriate activity page
    switch (activity.type) {
      case 'lesson':
        navigate(`/adaptive/${childId}/${subject}?phase=instruction&level=${levelNum}`);
        break;
      case 'quiz':
        navigate(`/adaptive/${childId}/${subject}?phase=practice&level=${levelNum}`);
        break;
      case 'game':
        navigate(`/games/${childId}?subject=${subject}&level=${levelNum}`);
        break;
      case 'puzzle':
        navigate(`/games/${childId}?subject=${subject}&tab=puzzle&level=${levelNum}`);
        break;
      case 'assessment':
        navigate(`/adaptive/${childId}/${subject}?phase=assessment&level=${levelNum}`);
        break;
      default:
        break;
    }
  };

  const isActivityUnlocked = (levelNum, activityIndex) => {
    if (levelNum < currentLevel) return true;
    if (levelNum === currentLevel && activityIndex <= currentActivity) return true;
    return false;
  };

  const isActivityCompleted = (levelNum, activityIndex) => {
    if (levelNum < currentLevel) return true;
    if (levelNum === currentLevel && activityIndex < currentActivity) return true;
    return false;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {showConfetti && <Confetti recycle={false} numberOfPieces={200} />}
      
      {/* Header */}
      <header className={`${config.bgColor} text-white`}>
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/learn/${childId}`}>
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/20">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <config.icon className="w-7 h-7" />
                </div>
                <div>
                  <h1 className="font-fredoka text-2xl font-bold">{config.name} Journey</h1>
                  <p className="text-white/80">{child?.name}'s Learning Path</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-white/80 text-sm">Total XP</p>
                <p className="font-bold text-xl flex items-center gap-1">
                  <Star className="w-5 h-5" />
                  {curriculum?.totalXP || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Overview */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-slate-600">Level {currentLevel} Progress</span>
            <Progress 
              value={(currentActivity / 5) * 100} 
              className="flex-1 h-3"
            />
            <span className="text-sm text-slate-500">{currentActivity}/5 activities</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Levels */}
        <div className="space-y-8">
          {[1, 2, 3].map((levelNum) => {
            const isLevelUnlocked = levelNum <= currentLevel;
            const isLevelCompleted = levelNum < currentLevel;
            const activities = LEVEL_ACTIVITIES[levelNum];

            return (
              <motion.div
                key={levelNum}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: levelNum * 0.1 }}
              >
                <Card className={`${!isLevelUnlocked ? 'opacity-60' : ''}`}>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isLevelCompleted 
                            ? 'bg-green-500 text-white' 
                            : isLevelUnlocked 
                              ? config.bgColor + ' text-white'
                              : 'bg-slate-200 text-slate-400'
                        }`}>
                          {isLevelCompleted ? (
                            <CheckCircle className="w-6 h-6" />
                          ) : isLevelUnlocked ? (
                            <span className="font-bold">{levelNum}</span>
                          ) : (
                            <Lock className="w-5 h-5" />
                          )}
                        </div>
                        <div>
                          <CardTitle className="text-lg">
                            Level {levelNum}: {levelNum === 1 ? 'Beginner' : levelNum === 2 ? 'Intermediate' : 'Advanced'}
                          </CardTitle>
                          {isLevelCompleted && (
                            <Badge variant="secondary" className="mt-1 bg-green-100 text-green-700">
                              <CheckCircle className="w-3 h-3 mr-1" /> Completed
                            </Badge>
                          )}
                        </div>
                      </div>
                      {isLevelCompleted && (
                        <div className="flex items-center gap-1 text-amber-500">
                          <Trophy className="w-5 h-5" />
                          <span className="font-bold">Level Mastered!</span>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-5 gap-3">
                      {activities.map((activity, idx) => {
                        const unlocked = isActivityUnlocked(levelNum, idx);
                        const completed = isActivityCompleted(levelNum, idx);
                        const isCurrent = levelNum === currentLevel && idx === currentActivity;

                        return (
                          <motion.button
                            key={idx}
                            whileHover={unlocked ? { scale: 1.05 } : {}}
                            whileTap={unlocked ? { scale: 0.95 } : {}}
                            onClick={() => handleActivityClick(levelNum, idx)}
                            disabled={!unlocked}
                            className={`relative p-4 rounded-xl border-2 transition-all ${
                              completed
                                ? 'bg-green-50 border-green-300 text-green-700'
                                : isCurrent
                                  ? 'bg-amber-50 border-amber-300 text-amber-700 ring-2 ring-amber-400'
                                  : unlocked
                                    ? 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md'
                                    : 'bg-slate-50 border-slate-100 text-slate-400 cursor-not-allowed'
                            }`}
                            data-testid={`activity-${levelNum}-${idx}`}
                          >
                            <div className="flex flex-col items-center gap-2">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                completed
                                  ? 'bg-green-500 text-white'
                                  : isCurrent
                                    ? 'bg-amber-500 text-white'
                                    : unlocked
                                      ? 'bg-slate-100'
                                      : 'bg-slate-200'
                              }`}>
                                {completed ? (
                                  <CheckCircle className="w-5 h-5" />
                                ) : (
                                  <activity.icon className="w-5 h-5" />
                                )}
                              </div>
                              <span className="text-xs font-medium text-center leading-tight">
                                {activity.name}
                              </span>
                              <span className={`text-xs ${completed ? 'text-green-600' : 'text-slate-400'}`}>
                                +{activity.xp} XP
                              </span>
                            </div>
                            {!unlocked && (
                              <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-xl">
                                <Lock className="w-5 h-5 text-slate-400" />
                              </div>
                            )}
                            {isCurrent && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full animate-pulse" />
                            )}
                          </motion.button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Completion Reward */}
        {curriculum?.levelsCompleted === 3 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mt-8"
          >
            <Card className="bg-gradient-to-r from-amber-400 to-orange-500 text-white">
              <CardContent className="p-8 text-center">
                <Trophy className="w-16 h-16 mx-auto mb-4" />
                <h2 className="font-fredoka text-3xl font-bold mb-2">Subject Mastered!</h2>
                <p className="text-lg opacity-90 mb-4">
                  Congratulations! You've completed all levels in {config.name}!
                </p>
                <div className="flex justify-center gap-4">
                  <Badge className="bg-white/20 text-white text-lg px-4 py-2">
                    <Star className="w-5 h-5 mr-2" />
                    {curriculum.totalXP} Total XP
                  </Badge>
                  <Badge className="bg-white/20 text-white text-lg px-4 py-2">
                    <Award className="w-5 h-5 mr-2" />
                    {config.name} Expert
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default CurriculumFlow;
